import { useMemo } from 'react';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import { FilterCategory, FilterList } from '@/components/FilterList';
import { useQueryFiltersStore, useSourcesStore } from '@/stores';
import { useVideoSourceSelected } from '@/hooks';
import { DateTimeMode, getDateTimeLabel } from '@/utils/query-filters';
import { EngineService } from '@/services/engine';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { Person } from '@/types/person';
import { Vehicle } from '@/types/vehicle';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { getAlertRuleDisplayName } from '@/utils/alert-rules';
import {
    EventFilter,
    EventsBrowseBarConfig,
} from '@/features/BrowseBar/EventsBrowseBar.types';
import {
    OBJECT_CATEGORIES,
    OBJECT_CLASSES,
    STANDALONE_CLASSES,
    type ObjectCategory,
} from '@/features/ObjectTypeFilter';

interface EventFilterViewProps {
    config: EventsBrowseBarConfig;
    relatedEventId?: string;
    onClearRelatedEvent?: () => void;
    vehicleId?: string;
    vehicle?: Vehicle;
    onClearVehicle?: () => void;
    personId?: string;
    person?: Person;
    onClearPerson?: () => void;
}

export function EventFilterView({
    config,
    relatedEventId,
    onClearRelatedEvent,
    vehicleId,
    vehicle,
    onClearVehicle,
    personId,
    person,
    onClearPerson,
}: EventFilterViewProps) {
    const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.EVENTS]);
    const filters = useQueryFiltersStore((state) => state.filters);
    const selectedAlertRuleIds = useQueryFiltersStore(
        (state) => state.selectedAlertRuleIds,
    );
    const selectedObjectClassIds = useQueryFiltersStore(
        (state) => state.selectedObjectClassIds,
    );
    const clearDateTimeFilter = useQueryFiltersStore(
        (state) => state.clearDateTimeFilter,
    );
    const setConfidenceFilter = useQueryFiltersStore(
        (state) => state.setConfidenceFilter,
    );
    const clearAlertRuleSelection = useQueryFiltersStore(
        (state) => state.clearAlertRuleSelection,
    );
    const toggleAlertRuleSelection = useQueryFiltersStore(
        (state) => state.toggleAlertRuleSelection,
    );
    const setObjectClassSelection = useQueryFiltersStore(
        (state) => state.setObjectClassSelection,
    );
    const clearObjectClassSelection = useQueryFiltersStore(
        (state) => state.clearObjectClassSelection,
    );
    const selected = useSourcesStore((state) => state.selected);
    const sourcesMap = useSourcesStore((state) => state.sourcesMap);
    const toggleSource = useSourcesStore((state) => state.toggleSource);
    const clearSelection = useSourcesStore((state) => state.clearSelection);
    const videoSourceSelected = useVideoSourceSelected(selected);

    const activeFilters = useMemo(() => {
        const filtersSet = new Set(config.filters ?? []);
        return {
            showConfidence: filtersSet.has(EventFilter.CONFIDENCE),
            showDateRange: filtersSet.has(EventFilter.DATE_RANGE),
            showSource: filtersSet.has(EventFilter.SOURCE),
            showAlertRule: filtersSet.has(EventFilter.ALERT_RULE),
            showObjectType: filtersSet.has(EventFilter.OBJECT_TYPE),
        };
    }, [config.filters]);

    const selectedRuleIds = useMemo(
        () => Array.from(selectedAlertRuleIds),
        [selectedAlertRuleIds],
    );

    const alertRulesKey: [string, string[]] | null =
        activeFilters.showAlertRule && selectedRuleIds.length > 0
            ? [CACHE_KEYS.ALERT_RULES_ALL, selectedRuleIds]
            : null;

    const { data: alertRules } = useSWR(
        alertRulesKey,
        ([, ids]) =>
            Promise.all(ids.map((id) => EngineService.getAlertRule(id))),
        { revalidateOnFocus: false },
    );

    const availableAlertRules = alertRules ?? [];

    const selectedObjectTypeItems = useMemo(() => {
        const isCategorySelected = (category: ObjectCategory) =>
            category.classes.every((objectClass) =>
                selectedObjectClassIds.has(objectClass.id),
            );
        const removeCategory = (category: ObjectCategory) => {
            const updated = new Set(selectedObjectClassIds);
            category.classes.forEach((objectClass) =>
                updated.delete(objectClass.id),
            );
            setObjectClassSelection(updated);
        };

        const standaloneItems = STANDALONE_CLASSES.filter(
            isCategorySelected,
        ).map((category) => ({
            id: `standalone-${category.id}`,
            label: t(`events:filter.${category.name}`, {
                defaultValue: category.name,
            }),
            onRemove: () => removeCategory(category),
        }));

        const categoryItems = OBJECT_CATEGORIES.filter(isCategorySelected).map(
            (category) => ({
                id: `category-${category.id}`,
                label: t(
                    `common:objectTypeFilter.categories.${category.name}`,
                    {
                        defaultValue: category.name,
                    },
                ),
                onRemove: () => removeCategory(category),
            }),
        );

        const groupedClassIds = new Set(
            [...STANDALONE_CLASSES, ...OBJECT_CATEGORIES]
                .filter(isCategorySelected)
                .flatMap((category) => category.classes.map(({ id }) => id)),
        );
        const ungroupedItems = OBJECT_CLASSES.filter(
            (objectClass) =>
                selectedObjectClassIds.has(objectClass.id) &&
                !groupedClassIds.has(objectClass.id),
        ).map((objectClass) => ({
            id: `class-${objectClass.id}`,
            label: t(`events:filter.${objectClass.name}`),
            onRemove: () => {
                const updated = new Set(selectedObjectClassIds);
                updated.delete(objectClass.id);
                setObjectClassSelection(updated);
            },
        }));

        return [...standaloneItems, ...categoryItems, ...ungroupedItems].sort(
            (first, second) => first.label.localeCompare(second.label),
        );
    }, [selectedObjectClassIds, setObjectClassSelection, t]);

    const alertRuleLabels = useMemo(
        () =>
            new Map(
                availableAlertRules.map((rule) => [
                    rule.id,
                    getAlertRuleDisplayName(rule),
                ]),
            ),
        [availableAlertRules],
    );

    const selectedSourceItems = useMemo(() => {
        const items = Array.from(selected).map((sourceId) => {
            const source = sourcesMap.get(sourceId);
            return {
                id: sourceId,
                label: source?.displayName ?? source?.name ?? sourceId,
                onRemove: () => toggleSource(sourceId),
            };
        });

        return items.sort((a, b) => a.label.localeCompare(b.label));
    }, [selected, sourcesMap, toggleSource]);

    const selectedAlertRuleItems = useMemo(() => {
        const items = selectedRuleIds.map((alertRuleId) => ({
            id: alertRuleId,
            label: alertRuleLabels.get(alertRuleId) ?? alertRuleId,
            onRemove: () => toggleAlertRuleSelection(alertRuleId),
        }));

        return items.sort((a, b) => a.label.localeCompare(b.label));
    }, [selectedRuleIds, alertRuleLabels, toggleAlertRuleSelection]);

    const labels = useMemo(
        () => ({
            title: t('common:browseBar.activeFilters'),
            date: t('common:browseBar.dateAndTime'),
            alertRules: t('common:browseBar.alertRules'),
            sources: t('common:browseBar.sources'),
            confidence: t('common:browseBar.confidenceLevel'),
            objectType: t('common:browseBar.objectType'),
            relatedTo: t('common:browseBar.relatedTo'),
            vehicle: t('common:browseBar.vehicle'),
            person: t('common:browseBar.person'),
        }),
        [t],
    );

    const categories: FilterCategory[] = useMemo(() => {
        const list: FilterCategory[] = [];

        if (relatedEventId && onClearRelatedEvent) {
            list.push({
                name: labels.relatedTo,
                items: [
                    {
                        id: relatedEventId,
                        label: relatedEventId,
                        onRemove: onClearRelatedEvent,
                    },
                ],
                onClearAll: onClearRelatedEvent,
            });
        }

        if (vehicleId && vehicle && onClearVehicle) {
            const vehicleLabel =
                vehicle.name || vehicle.licensePlate || vehicleId;
            list.push({
                name: labels.vehicle,
                items: [
                    {
                        id: vehicleId,
                        label: vehicleLabel,
                        onRemove: onClearVehicle,
                    },
                ],
                onClearAll: onClearVehicle,
            });
        }

        if (personId && person && onClearPerson) {
            const personLabel = person.name || personId;
            list.push({
                name: labels.person,
                items: [
                    {
                        id: personId,
                        label: personLabel,
                        onRemove: onClearPerson,
                    },
                ],
                onClearAll: onClearPerson,
            });
        }

        if (
            activeFilters.showDateRange &&
            !videoSourceSelected &&
            filters.dateTime.mode !== DateTimeMode.ALL
        ) {
            list.push({
                name: labels.date,
                items: [
                    {
                        label: getDateTimeLabel(filters.dateTime),
                        onRemove: clearDateTimeFilter,
                    },
                ],
                onClearAll: clearDateTimeFilter,
            });
        }

        if (activeFilters.showAlertRule && selectedAlertRuleItems.length > 0) {
            list.push({
                name: labels.alertRules,
                items: selectedAlertRuleItems,
                onClearAll: clearAlertRuleSelection,
            });
        }

        if (activeFilters.showSource && selectedSourceItems.length > 0) {
            list.push({
                name: labels.sources,
                items: selectedSourceItems,
                ...(!config.requireSource && { onClearAll: clearSelection }),
            });
        }

        if (activeFilters.showConfidence && filters.confidence.enabled) {
            list.push({
                name: labels.confidence,
                items: [
                    {
                        label: `${filters.confidence.min}% - ${filters.confidence.max}%`,
                        onRemove: () => setConfidenceFilter(false),
                    },
                ],
                onClearAll: () => setConfidenceFilter(false),
            });
        }

        if (
            activeFilters.showObjectType &&
            selectedObjectTypeItems.length > 0
        ) {
            list.push({
                name: labels.objectType,
                items: selectedObjectTypeItems,
                onClearAll: clearObjectClassSelection,
            });
        }

        return list;
    }, [
        activeFilters.showAlertRule,
        activeFilters.showConfidence,
        activeFilters.showDateRange,
        activeFilters.showObjectType,
        activeFilters.showSource,
        clearAlertRuleSelection,
        clearDateTimeFilter,
        clearObjectClassSelection,
        clearSelection,
        config.requireSource,
        filters.confidence.enabled,
        filters.confidence.max,
        filters.confidence.min,
        filters.dateTime,
        labels,
        onClearRelatedEvent,
        relatedEventId,
        vehicleId,
        vehicle,
        onClearVehicle,
        personId,
        person,
        onClearPerson,
        selectedAlertRuleItems,
        selectedObjectTypeItems,
        selectedSourceItems,
        setConfidenceFilter,
        videoSourceSelected,
    ]);

    return <FilterList title={labels.title} categories={categories} />;
}
